import {
  PortalDayFragment,
  PortalPunchFragment,
  PunchKindEnum,
  usePortalClassificationContestMutation,
  usePortalPunchJustifyMutation,
} from "@dashboard/graphql";
import { Box, Button, Text, Textarea } from "@saleor/macaw-ui-next";
import { ReactNode, useState } from "react";
import { MessageDescriptor, useIntl } from "react-intl";

import { portalLabels as l } from "./labels";
import { portalMessages as m } from "./messages";
import { formatDateTime, formatDay, formatMinutes, formatTime } from "./utils";

export const Section = ({
  title,
  actions,
  children,
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}) => (
  <Box
    as="section"
    display="flex"
    flexDirection="column"
    gap={3}
    padding={4}
    borderWidth={1}
    borderStyle="solid"
    borderColor="default1"
    borderRadius={4}
    backgroundColor="default1"
  >
    {(title || actions) && (
      <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
        {title && (
          <Text size={5} fontWeight="bold">
            {title}
          </Text>
        )}
        <Box __flex={1} />
        {actions}
      </Box>
    )}
    {children}
  </Box>
);

export const Page = ({ children }: { children: ReactNode }) => (
  <Box
    display="flex"
    flexDirection="column"
    gap={4}
    padding={4}
    __maxWidth={1100}
    width="100%"
    marginX="auto"
  >
    {children}
  </Box>
);

const GENERIC_ERRORS = new Set(["err_invalid", "err_required"]);

/** API errors: specific codes are shown in the UI language; generic ones
 * keep the server's (Portuguese) detail, which is more precise. */
export const ErrorText = ({
  errors,
}: {
  errors?: Array<{ message: string | null; code?: string | null }> | null;
}) => {
  const intl = useIntl();

  if (!errors?.length) {
    return null;
  }

  const text = (error: { message: string | null; code?: string | null }) => {
    const key = `err_${(error.code ?? "").toLowerCase()}`;
    const known = (l as Record<string, MessageDescriptor>)[key];

    if (!known) {
      return error.message ?? "";
    }

    // Generic codes: the server's Portuguese detail says more, when readable.
    if (GENERIC_ERRORS.has(key) && intl.locale.startsWith("pt") && error.message) {
      return error.message;
    }

    return intl.formatMessage(known);
  };

  return (
    <Text size={2} color="critical1">
      {errors.map(text).join(" ")}
    </Text>
  );
};

export const KIND_MESSAGES: Record<PunchKindEnum, MessageDescriptor> = {
  [PunchKindEnum.IN]: m.kindIn,
  [PunchKindEnum.BREAK_START]: m.kindBreakStart,
  [PunchKindEnum.BREAK_END]: m.kindBreakEnd,
  [PunchKindEnum.OUT]: m.kindOut,
};

export const useFlagLabel = () => {
  const intl = useIntl();

  return (flag: string) => {
    const message = (m as Record<string, MessageDescriptor>)[`flag_${flag}`];

    return message ? intl.formatMessage(message) : flag;
  };
};

export const Flags = ({ flags }: { flags: string[] }) => {
  const label = useFlagLabel();

  if (!flags.length) {
    return null;
  }

  return (
    <Box display="flex" gap={1} flexWrap="wrap">
      {flags.map(flag => (
        <Box key={flag} paddingX={2} borderRadius={4} backgroundColor="warning1" __fontSize="12px">
          {label(flag)}
        </Box>
      ))}
    </Box>
  );
};

const InlineText = ({
  placeholder,
  onSubmit,
  onCancel,
  loading,
  defaultValue = "",
}: {
  placeholder?: string;
  onSubmit: (text: string) => void;
  onCancel: () => void;
  loading?: boolean;
  defaultValue?: string;
}) => {
  const intl = useIntl();
  const [text, setText] = useState(defaultValue);

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Textarea
        size="small"
        rows={2}
        value={text}
        placeholder={placeholder}
        maxLength={2000}
        onChange={event => setText(event.target.value)}
      />
      <Box display="flex" gap={2}>
        <Button
          size="small"
          variant="primary"
          disabled={!text.trim() || loading}
          onClick={() => onSubmit(text.trim())}
        >
          {intl.formatMessage(m.send)}
        </Button>
        <Button size="small" variant="secondary" onClick={onCancel}>
          {intl.formatMessage(m.cancel)}
        </Button>
      </Box>
    </Box>
  );
};

export { InlineText };

export const PunchRow = ({
  punch,
  canJustify,
  actions,
  superseded,
}: {
  punch: PortalPunchFragment;
  canJustify?: boolean;
  actions?: ReactNode;
  // Replaced or voided by a later correction: still shown, struck through.
  superseded?: boolean;
}) => {
  const intl = useIntl();
  const [justifying, setJustifying] = useState(false);
  const [justify, justifyOpts] = usePortalPunchJustifyMutation({
    onCompleted: data => {
      if (!data.punchJustify?.errors.length) {
        setJustifying(false);
      }
    },
  });
  const struck = superseded;

  return (
    <Box display="flex" flexDirection="column" gap={1} paddingY={1}>
      <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
        <Text
          size={3}
          fontWeight="bold"
          __minWidth="70px"
          __textDecoration={struck ? "line-through" : undefined}
        >
          {formatTime(punch.effectiveAt)}
        </Text>
        <Text size={3}>{intl.formatMessage(KIND_MESSAGES[punch.kind])}</Text>
        {punch.isCorrection && (
          <Text size={2} color="accent1">
            {intl.formatMessage(punch.voids ? m.voided : m.correction)} ·{" "}
            {intl.formatMessage(m.by, { name: punch.createdByName })} ·{" "}
            {formatDateTime(punch.recordedAt)}
          </Text>
        )}
        <Flags flags={punch.flags} />
        <Box __flex={1} />
        <Text size={1} color="default2" title={punch.hash} __fontFamily="monospace">
          #{punch.seq} · {punch.hash.slice(0, 12)}
        </Text>
        {canJustify && (punch.flags.length > 0 || punch.isCorrection) && !justifying && (
          <Button size="small" variant="tertiary" onClick={() => setJustifying(true)}>
            {intl.formatMessage(punch.isCorrection ? m.contest : m.justify)}
          </Button>
        )}
        {actions}
      </Box>
      {punch.isCorrection && punch.reason && (
        <Text size={2} color="default2">
          {intl.formatMessage(m.reason)}: {punch.reason}
        </Text>
      )}
      {punch.claims.map(item => (
        <Text key={item.id} size={2} color="default2">
          {intl.formatMessage(m.claimLine, {
            time: formatTime(item.claimedAt).slice(0, 5),
            status: item.status.toLowerCase(),
          })}
          {item.decisionNote && ` — ${item.decisionNote}`}
        </Text>
      ))}
      {punch.justifications.map(item => (
        <Text key={item.id} size={2} color="default2">
          “{item.text}” · {formatDateTime(item.createdAt)}
        </Text>
      ))}
      {justifying && (
        <InlineText
          placeholder={intl.formatMessage(m.justifyPlaceholder)}
          loading={justifyOpts.loading}
          onCancel={() => setJustifying(false)}
          onSubmit={text => justify({ variables: { id: punch.id, text } })}
        />
      )}
      <ErrorText errors={justifyOpts.data?.punchJustify?.errors} />
    </Box>
  );
};

const CLASSIFICATION_MESSAGES: Record<string, MessageDescriptor> = {
  OVERTIME: l.clsOvertime,
  PRESENCE_WITHOUT_WORK: l.clsPresence,
  UNAUTHORIZED_STAY: l.clsUnauthorized,
  ADJUSTMENT: l.clsAdjustment,
  TIME_BANK: l.clsTimeBank,
  JUSTIFIED_ABSENCE: l.clsJustified,
  UNJUSTIFIED_ABSENCE: l.clsUnjustified,
  COMPENSATION: l.clsCompensation,
};

export const useClassificationLabel = () => {
  const intl = useIntl();

  return (kind: string) =>
    CLASSIFICATION_MESSAGES[kind] ? intl.formatMessage(CLASSIFICATION_MESSAGES[kind]) : kind;
};

const ClassificationRow = ({
  row,
  canContest,
  onChanged,
}: {
  row: PortalDayFragment["classifications"][number];
  canContest?: boolean;
  onChanged?: () => void;
}) => {
  const intl = useIntl();
  const classificationLabel = useClassificationLabel();
  const [open, setOpen] = useState(false);
  const [contest, contestOpts] = usePortalClassificationContestMutation({
    onCompleted: data => {
      if (!data.classificationContest?.errors.length) {
        setOpen(false);
        onChanged?.();
      }
    },
  });

  return (
    <Box display="flex" flexDirection="column" gap={1}>
      <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
        <Text size={2}>
          {intl.formatMessage(m.classification, {
            minutes: formatMinutes(row.minutes),
            kind: classificationLabel(row.kind),
            reason: row.reason,
          })}{" "}
          · {intl.formatMessage(m.by, { name: row.decidedByName })}
        </Text>
        {canContest && !open && (
          <Button size="small" variant="tertiary" onClick={() => setOpen(true)}>
            {intl.formatMessage(m.contest)}
          </Button>
        )}
      </Box>
      {row.contests.map((text, index) => (
        <Text key={index} size={2} color="default2">
          ↳ “{text}”
        </Text>
      ))}
      {open && (
        <InlineText
          loading={contestOpts.loading}
          onCancel={() => setOpen(false)}
          onSubmit={text => contest({ variables: { id: row.id, text } })}
        />
      )}
      <ErrorText errors={contestOpts.data?.classificationContest?.errors} />
    </Box>
  );
};

export const DayCard = ({
  day,
  worker,
  onChanged,
  punchActions,
  footer,
}: {
  day: PortalDayFragment;
  worker?: boolean;
  onChanged?: () => void;
  punchActions?: (punch: PortalPunchFragment) => ReactNode;
  footer?: ReactNode;
}) => {
  const intl = useIntl();
  const superseded = new Set(day.punches.map(p => p.correctsId).filter(Boolean));

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={1}
      paddingY={3}
      borderBottomWidth={1}
      borderBottomStyle="solid"
      borderColor="default1"
    >
      <Box display="flex" gap={3} alignItems="baseline" flexWrap="wrap">
        <Text size={4} fontWeight="bold" __minWidth="90px">
          {formatDay(day.date)}
        </Text>
        <Text size={2} color="default2">
          {day.planned.length
            ? day.planned
                .map(
                  w =>
                    `${w.shiftName ? w.shiftName + " " : ""}${formatTime(w.start).slice(0, 5)}–${formatTime(w.end).slice(0, 5)}`,
                )
                .join(", ")
            : intl.formatMessage(m.dayOff)}
        </Text>
        <Box __flex={1} />
        <Text size={2}>
          {intl.formatMessage(m.registered)}: <b>{formatMinutes(day.workedMinutes)}</b>
          {day.offScheduleMinutes > 0 &&
            ` · ${intl.formatMessage(m.offSchedule)}: ${formatMinutes(day.offScheduleMinutes)}`}
          {day.missingMinutes > 0 &&
            ` · ${intl.formatMessage(m.missing)}: ${formatMinutes(day.missingMinutes)}`}
          {day.toleratedMissingMinutes > 0 &&
            ` · ${intl.formatMessage(m.tolerated)}: ${formatMinutes(day.toleratedMissingMinutes)}`}
        </Text>
        <Flags flags={day.flags} />
      </Box>
      {day.punches.length === 0 ? (
        <Text size={2} color="default2">
          {intl.formatMessage(m.noPunches)}
        </Text>
      ) : (
        day.punches.map(punch => (
          <PunchRow
            key={punch.id}
            punch={punch}
            canJustify={worker}
            superseded={superseded.has(punch.id)}
            actions={punchActions?.(punch)}
          />
        ))
      )}
      {day.classifications.map(row => (
        <ClassificationRow key={row.id} row={row} canContest={worker} onChanged={onChanged} />
      ))}
      {footer}
    </Box>
  );
};
